import { chakra, shouldForwardProp } from '@chakra-ui/react';
import { getAsset, getTenantByHost } from '@pelicargo/tenants';
import { modelUtils } from '@pelicargo/utils';
import { isValidMotionProp, motion, useReducedMotion } from 'framer-motion';
import { useState } from 'react';

const MotionBox = chakra(motion.div, {
  shouldForwardProp: (prop) => isValidMotionProp(prop) || shouldForwardProp(prop),
});

// Everything here is the tenant's *static* branding, read straight from `@pelicargo/tenants` - it is
// on hand before the DB theme, which is what is loading. Deliberately not `useCustomerAsset` or the
// `primary` colour token: those switch to the DB values the moment the theme lands, which would swap
// the logo mid-fade, and the token's last fallback is Pelicargo's orange for a tenant with no static
// `primary` scale.
// `customerTheme` is a union of per-tenant literal shapes, not every one of which declares `colors`;
// read it through one loose view.
type StaticTheme = { colors?: { primary?: Record<string, string> } };

const tenant = getTenantByHost();
const staticTheme = tenant?.customerTheme satisfies StaticTheme | undefined;
const LOGO_SRC = getAsset(modelUtils.customerTheme.CUSTOMER_ASSET_SLOTS.logo);
const LOGO_DIMS = tenant?.customerTheme?.fullLogoDims;
const ACCENT = staticTheme?.colors?.primary?.['500'] ?? 'gray.400';

const EASE_OUT = [0.16, 1, 0.3, 1];

// Transitions ride inside the targets rather than on a `transition` prop, which a `chakra()`-wrapped
// motion component reads as Chakra's CSS `transition` style prop.
const SWEEP = { x: ['-100%', '250%'], transition: { duration: 1.2, ease: 'easeInOut', repeat: Infinity } };
// Reduced motion: no travel, just a slow pulse in place.
const REDUCED_SWEEP = {
  x: '75%',
  opacity: [0.4, 1, 0.4],
  transition: { duration: 2, ease: 'easeInOut', repeat: Infinity },
};

/**
 * The tenant-branded screen `ThemeGate` shows while a non-Peli tenant's theme loads: its logo over
 * an indeterminate progress bar in its own accent colour.
 *
 * Fixed over the viewport so it can fade out on top of the app as the app mounts underneath, rather
 * than cutting to it. Mount it inside `<AnimatePresence>` for the exit to play.
 *
 * Literal radii and raw palette values only: the radii scale and semantic colour tokens are
 * `--peli-*` variables whose fallbacks are Pelicargo's.
 */
export const ThemeLoader = () => {
  const reduceMotion = useReducedMotion();
  // The logo fades in once decoded rather than popping in partway through a paint; a broken logo
  // simply never shows, leaving the bar on its own.
  const [logoReady, setLogoReady] = useState(false);

  return (
    <MotionBox
      position="fixed"
      inset="0"
      zIndex="overlay"
      bg="white"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } }}
      exit={{ opacity: 0, transition: { duration: 0.4, ease: 'easeOut' } }}
      role="status"
      aria-live="polite"
      aria-label={`Loading ${tenant?.name ?? ''}`.trim()}
    >
      <MotionBox
        as="img"
        src={LOGO_SRC}
        alt=""
        h={LOGO_DIMS?.height ? `${Number(LOGO_DIMS.height) * 1.5}px` : '12'}
        maxW="60vw"
        objectFit="contain"
        onLoad={() => setLogoReady(true)}
        initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.94, y: reduceMotion ? 0 : 6 }}
        animate={logoReady ? { opacity: 1, scale: 1, y: 0, transition: { duration: 0.5, ease: EASE_OUT } } : undefined}
      />

      <MotionBox
        w="40"
        h="3px"
        borderRadius="9999px"
        overflow="hidden"
        bg="gray.100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.4, delay: 0.15 } }}
      >
        <MotionBox
          h="full"
          w="40%"
          borderRadius="9999px"
          bg={ACCENT}
          initial={{ x: '-100%' }}
          animate={reduceMotion ? REDUCED_SWEEP : SWEEP}
        />
      </MotionBox>
    </MotionBox>
  );
};
